'use strict';

let instance;
const carrierSettings = require('./carrier.settings');
const ShoppingCart = require('od-modules/cart/cart');

const DEFAULT_TRACK_CARRIER = 'NA';
const BOARDING_TYPES = new Odigeo.Enum(['BO_SB', 'BO_PB', 'BO_HB']);
const PRIME_DISCOUNT_AMOUNT_THRESHOLD = 10;
import {dispatch} from 'od-modules/secure_store/store';
import {SET_BOARDING_OPTIONS} from 'od-modules/secure_store/actions';

const BoardingOptionsCollection = Odigeo.Model.extend({
    dataUrl: 'getboardingoptions',
    loaded: false,
    loading: false,
    interacted: false,
    selectedByUser: false,
    defaultType: 'BO_SB',
    emptyType: 'NA',
    load(params) {
        if (this.isABEnabled() && params.load) {
            this.populate();
        }
    },
    url() {
        return Odigeo.Settings.getProperty('serviceUrlFormatter')(this.dataUrl);
    },
    populate() {
        if (!this.loaded && !this.loading) {
            this.fetch(this.getDataRequestSettings());
        }
    },
    getDataRequestSettings() {
        this.loading = true;

        return {
            type: 'GET',
            cache: false,
            dataType: 'json',
            contentType: 'application/json',
            success: Odigeo._.bind(this.requestSuccessHandler, this),
        };
    },
    requestSuccessHandler() {
        this.loading = false;
        this.loaded = true;
        const {carriers, collection, carrierCode, tripType} = this.attributes;
        dispatch({
            type: SET_BOARDING_OPTIONS,
            entry: {carriers, collection, carrierCode, tripType},
        });
        this.processData();
        this.triggerBoardingOptionsLoaded();
        this.trigger('responseIsOK');
    },
    triggerBoardingOptionsLoaded() {
        if (this.isPriorityOptionsEnabled()) {
            Odigeo.trigger('boarding-options:boardingOptionsLoaded', Odigeo._.extend(this.toJSON(), {
                trackingData: this.getTrackData(),
            }));
        }
    },
    setData(data = {}) {
        this.set(data);
        this.processData();
        this.triggerBoardingOptionsLoaded();

        return this;
    },
    getDesktopAB() {
        return Odigeo._.get(Odigeo, 'AB.getBoardingOptionsWidget', 1);
    },
    getMobileAB() {
        return Odigeo._.get(Odigeo, 'AB.boardingOptionsMobile', 1);
    },
    isABEnabled() {
        return Odigeo.SessionScope.flow === 'mobile' ?
            this.getMobileAB()
            : Odigeo._.includes([2, 3], this.getDesktopAB());
    },
    isLoaded() {
        return this.loaded;
    },
    processData() {
        const isMobile = Odigeo.SessionScope.flow === 'mobile';
        const isTravelFusionCabinBags = isMobile ? Odigeo.AB.isTravelFusionCabinBagsMobile : Odigeo.AB.isTravelFusionCabinBagsDesktop;
        let processedCarrierSettings;
        if (isTravelFusionCabinBags) {
            processedCarrierSettings = carrierSettings.get();
        } else {
            processedCarrierSettings = Odigeo._.get(carrierSettings.get(), this.getCarrier().toLowerCase());
        }
        this.setPrimeInfo();
        this.set({
            selectedType: this.isPriorityOptionsEnabled() ? this.getSelectedType() || this.defaultType : this.emptyType,
            defaultType: this.defaultType,
            fee: this.getFee(),
            trackingData: this.getTrackData(),
            carrierSettings: processedCarrierSettings,
        });
    },
    isPriorityOptionsEnabled() {
        return this.isABEnabled() &&
            this.hasPriorityOptionsEnabledForAllSections() &&
            this.isProviderCompatible() &&
            this.isCarrierCompatible();
    },
    isProviderCompatible() {
        return Odigeo.SessionScope.flow === 'mobile' ?
            this.getMobileAB()
            : true;
    },
    isCarrierCompatible: function() {
        return !Odigeo._.isEmpty(this.getCarrierSettings());
    },
    isPriorityOptionsActive() {
        return this.isPriorityOptionsEnabled() && Odigeo._.get(this.get('descriptors'), 'length', 0) > 0;
    },
    hasPriorityOptionsEnabledForAllSections() {
        return this.get('priorityBoardingInAllSections');
    },
    getSelectedType() {
        if (!this.isPriorityOptionsEnabled()) {
            return this.emptyType;
        }

        return this.isPriorityOptionsActive() ? this.getSelectedTypeFromSelection() : this.defaultType;
    },
    haveAtLeastOneChargeableOptionInAllSections() {
        const descriptors = this.get('descriptors') || [];

        return descriptors.reduce((acc, section) => {
            return acc
                && (section.boardingOptions || []).find((option) => option.type !== this.defaultType) !== undefined;
        }, descriptors.length > 0);
    },
    getSelectedTypeFromSelection() {
        return this.getUserSelectionType() || this.getSelectionType();
    },
    getFee() {
        const selection = this.get('selection') || [];
        const total = Odigeo._.reduce(selection, (fee, segment) => {
            return fee + Math.abs(segment.totalPrice - segment.providerPrice);
        }, 0);

        return selection.length > 0
            ? total / selection.length
            : 0;
    },
    getDapiFees() {
        const selection = this.get('selection') || [];
        const defaultFees = {fee: 0, primeFee: 0};

        if (!selection.length) {
            return defaultFees;
        }

        const boardingOptionsMap = (this.get('descriptors') || []).reduce((acc, cur) => ({
            ...acc,
            [cur.section]: cur.boardingOptions.reduce((acc, cur) => ({
                ...acc,
                [cur.type]: cur,
            }), {}),
        }), {});

        const totalFees = selection.reduce((acc, {section, type}) => {
            const {fee = 0, primeFee = 0} = Odigeo._.get(boardingOptionsMap[section], type, {});

            return {
                fee: acc.fee + fee,
                primeFee: acc.primeFee + primeFee,
            };
        }, defaultFees);

        return {fee: totalFees.fee / selection.length, primeFee: totalFees.primeFee / selection.length};
    },
    setInteraction() {
        this.interacted = true;
    },
    setSelectedByUser() {
        this.selectedByUser = true;
    },
    select(data = {}) {
        this.set('clientSelection', data);
        Odigeo.trigger('getTotalByFullprice', this);
        this.updateShoppingCart();
    },
    getDapiSelection() {
        const {fee, primeFee} = this.getDapiFees();

        return {
            fee,
            primeFee,
            selectedType: this.getSelectedType(),
            carrier: this.getCarrier(),
        };
    },
    updateShoppingCart() {
        const {fee: _fee = 0, primeFee = 0, selectedType = '', carrier = ''} = this.get('clientSelection') || this.getDapiSelection();

        if (selectedType === this.emptyType) {
            return;
        }

        const isCabinBagsPrimeDiscounts = Odigeo.AB.isCabinBagsPrimeDiscountsDesktop || Odigeo.AB.isCabinBagsPrimeDiscountsMobile;
        const totalPrimePrice = isCabinBagsPrimeDiscounts ? this.getSelectionPrice('totalPrimePrice') : 0;
        const usePrimePrice = totalPrimePrice > 0 || (isCabinBagsPrimeDiscounts && selectedType === this.defaultType);

        let total = this.getSelectionPrice();
        let fee = _fee;
        let primePerksFromClient = 0;
        let primePerksFromDapi = 0;

        if (usePrimePrice) {
            const isPriceWithDiscountSelected = Odigeo.Membership.isPriceWithoutDiscountSelected === 'false';

            primePerksFromClient = total - totalPrimePrice;
            primePerksFromDapi = this.getSelectionDapiPrice() - this.getSelectionDapiPrice('totalPrimePrice');
            total = totalPrimePrice;

            if (isPriceWithDiscountSelected) {
                fee = primeFee;
            }
        }

        ShoppingCart.builders.boardingOption.data({
            usePrimePrice,
            price: {
                total,
                fee,
            },
            perks: {
                fromClient: primePerksFromClient,
                fromDapi: primePerksFromDapi,
            },
            selection: selectedType,
            carrier,
        }).fromWidget().add();
    },
    getUserSelectionType() {
        return Odigeo._.get(this.get('clientSelection'), 'selectedType');
    },
    getSelectionType() {
        return Odigeo._.get(this.get('selection'), '[0].type', this.defaultType);
    },
    hasUserAddedAnything() {
        return this.getSelectedType() !== this.defaultType;
    },
    hasUserInteracted() {
        return this.interacted;
    },
    isSelectedByUser() {
        return this.selectedByUser;
    },
    mustShowNag() {
        return this.isPriorityOptionsActive()
            && this.haveAtLeastOneChargeableOptionInAllSections()
            && (!this.hasUserInteracted() || this.isNewCabinBagsNagLogicActive())
            && !this.hasUserAddedAnything()
            && (Odigeo.SessionScope.flow !== 'mobile' || Odigeo.AB.isCabinBagsNagMobile);
    },
    isNewCabinBagsNagLogicActive: function() {
        return Odigeo.AB.isCabinBagsNagMobile || Odigeo.AB.isCabinBagsNagCompactDesktop || this.isAnyNagsLogicVariationActive();
    },

    isAnyNagsLogicVariationActive: function() {
        return Odigeo.AB.nagsLogicVariationsDesktop > 1 || Odigeo.AB.nagsLogicVariationsMobile > 1;
    },
    getCompleteUserSelectionData() {
        return this.isPriorityOptionsEnabled() && this.hasUserAddedAnything()
            ? this.getCompoundSelection(this.getUserSelectionType() || this.defaultType)
            : [];
    },
    getCompoundSelection(type) {
        return Odigeo._.reduce(this.get('descriptors'), (selection, descriptor) => {
            return [...selection, ...this.getPaxSelection(descriptor, this.getRawType(descriptor, type))];
        }, []);
    },
    getPaxSelection(descriptor, rawType) {
        return this.getTravellers().map((traveller) => {
            return {
                section: descriptor.section,
                numPassenger: traveller,
                type: rawType,
            };
        });
    },
    getRawType(descriptor, type) {
        return Odigeo._.get(this.getDescriptorByType(descriptor, type), 'rawType');
    },
    getDescriptorByType(descriptor, type) {
        return Odigeo._.get(descriptor, 'boardingOptions', []).find((option) => option.type === type);
    },
    getTrackData() {
        const carrierFormatted = this.getCarrier().replace(/\./g, '-');
        const isPrimeView = this.get('primeInfo') ? this.get('primeInfo').isPrimeView : false;
        return {
            carrier: this.getDefaultIfEmpty(carrierFormatted, DEFAULT_TRACK_CARRIER),
            totalNumber: this.getAllSections().length,
            carrierNumber: this.getCarrierSectionNumber(this.getDefaultIfEmpty(this.getCarrierList(), [DEFAULT_TRACK_CARRIER])),
            selectedType: this.isPriorityOptionsEnabled() ? this.getSelectedType() || this.defaultType : this.emptyType,
            priorityOptionsEnabledForAllSections: this.hasPriorityOptionsEnabledForAllSections(),
            collection: this.get('collection'),
            maxdsc: isPrimeView ? `${this.get('primeInfo').discountAmount}-${this.get('primeInfo').discountDisplayType}` : '0-per',
            isPrimeAB: Odigeo.AB.isCabinBagsPrimeDiscountsDesktop || Odigeo.AB.isCabinBagsPrimeDiscountsMobile,
        };
    },
    getDefaultIfEmpty(obj, defaultValue) {
        return Odigeo._.isEmpty(obj) ? defaultValue : obj;
    },
    getCarrier() {
        return this.get('carrierCode') || '';
    },
    getCarrierList() {
        return this.get('carriers') || [];
    },
    getAllSections() {
        return Odigeo._.get(this.getFlightDetailsDTO(), 'allSections', []);
    },
    getFlightDetailsDTO() {
        return this.get('flightDetailsDTO');
    },
    getCarrierSectionNumber(carriers) {
        return Odigeo._.reduce(this.getAllSections(), (sum, section) => {
            return sum + (Odigeo._.includes(carriers, Odigeo._.get(section, 'carrier.code', '')) ? 1 : 0);
        }, 0, this);
    },
    getCarrierSections() {
        const sections = this.getAllSections();
        const ids = this.getCarrierSectionIds();

        return sections.length === ids.length ? 'ALL' : ids.join('-');
    },
    getCarrierSectionIds() {
        return Odigeo._.map(this.getCarrierToTrackSections(), (section) => {
            return parseInt(section.id, 10) + 1;
        });
    },
    getCarrierToTrackSections() {
        const carriers = this.getCarrierList();

        return Odigeo._.filter(this.getAllSections(), (section) => {
            return Odigeo._.includes(carriers, Odigeo._.get(section, 'carrier.code', ''));
        }, 0, this);
    },
    getTravellers() {
        return this.get('travellers') || [];
    },
    getNumTravellers() {
        return this.getTravellers().length || 0;
    },
    getUnitPrice(priceType) {
        return Odigeo._.get(this.get('collection'), [this.getSelectedType(), priceType], 0);
    },
    getUnitDapiPrice(priceType) {
        return Odigeo._.get(this.get('collection'), [this.getSelectionType(), priceType], 0);
    },
    getSelectionPrice(priceType = 'totalPrice') {
        return this.getNumTravellers() * this.getCarrierSectionNumber(this.getCarrierList()) * this.getUnitPrice(priceType);
    },
    getSelectionDapiPrice(priceType = 'totalPrice') {
        return this.getNumTravellers() * this.getCarrierSectionNumber(this.getCarrierList()) * this.getUnitDapiPrice(priceType);
    },
    getCarrierSettings() {
        return this.get('carrierSettings');
    },
    getTotalPrimeDiscountAmount() {
        const cabinBagOption = Odigeo._.get(this.get('collection'), [BOARDING_TYPES.BO_PB], {});
        const numTraveller = this.getNumTravellers();
        const carrierList = this.getCarrierList();
        const carrierSectionNumber = this.getCarrierSectionNumber(carrierList);
        const quantity = numTraveller * carrierSectionNumber;
        const totalPrice = cabinBagOption.totalPrice * quantity;
        const totalPrimePrice = cabinBagOption.totalPrimePrice * quantity;
        const totalDiscount = Math.floor(totalPrice - totalPrimePrice);

        return totalDiscount > 0 ? totalDiscount : 0;
    },

    getPrimeDiscountPercentage() {
        const cabinBagOption = Odigeo._.get(this.get('collection'), [BOARDING_TYPES.BO_PB], {});
        const unitPrice = cabinBagOption.totalPrice;
        const unitDiscount = unitPrice - cabinBagOption.totalPrimePrice;
        return unitPrice ? Math.ceil((unitDiscount * 100) / unitPrice) : 0;
    },
    setPrimeInfo() {
        const isCabinBagsPrimeDiscounts = Odigeo.AB.isCabinBagsPrimeDiscountsDesktop || Odigeo.AB.isCabinBagsPrimeDiscountsMobile;

        const discountAmount = this.getTotalPrimeDiscountAmount();
        const discountPercentage = this.getPrimeDiscountPercentage();

        const isPrimeProductOffered = Odigeo.Membership.isMembershipProductEnabled;
        const isUserPrimeMemberAndNotLogged = Odigeo.Membership.isUserPrimeMemberAndNotLogged;
        const isPriceWithDiscountSelected = Odigeo.Membership.isPriceWithoutDiscountSelected === 'false';
        const hasAValidDiscount = discountPercentage > 0;

        const memberName = Odigeo.Membership.membershipUserName;
        const isPrimeUser = memberName ? memberName.length > 0 : false;
        const isPrimeFareOffered = (isUserPrimeMemberAndNotLogged || isPrimeProductOffered) && isPriceWithDiscountSelected;

        const isDiscountDisplayPercentage = (discountPercentage >= PRIME_DISCOUNT_AMOUNT_THRESHOLD) || (discountPercentage >= discountAmount);
        const discountDisplayType = isDiscountDisplayPercentage ? 'per' : 'abs';
        const merchandisingBadgeDiscountText = isDiscountDisplayPercentage ?
            `-${discountPercentage}%`
            : Odigeo.Utils.formatCurrency(discountAmount, {...Odigeo.Utils.Currency, decimalPlaces: 0});

        const isPrimeView = isCabinBagsPrimeDiscounts && (isPrimeFareOffered || isPrimeUser) && hasAValidDiscount;

        this.set('primeInfo', {
            isPrimeView,
            isPrimeUser,
            discountPercentage,
            discountAmount,
            merchandisingBadgeDiscountText,
            discountDisplayType,
        });
    },
    getPrimeInfo() {
        return this.get('primeInfo');
    },
});

export const getInstance = (params) => {
    if (Odigeo._.isUndefined(instance)) {
        instance = new BoardingOptionsCollection();
    }

    instance.load(params || {});

    return instance;
};
